<template>
    <component
    :is="detailsComponent"
    :detailInfo="detailInfo"
    :payloadDetailModal="payloadDetailModal"
    :requestAction="requestAction"
    :buttonsTopData="buttonsTopData"
    :showFooter="showDetailsFooter"
    :buttonsFooterData="buttonsFooterData"
    :isMobile="isMobile"
    :triggerClose="triggerDetailsModalClose"
    @hideMe="emit('hideMe')" />
    <!-- Generic Centered Modal -->
    <modal-centered-generic
    v-if="showCenteredModal"
    negative
    :title="centeredModalTitle"
    :text="centeredModalText"
    :leftButtonText="t('approvals.signed.modal.centeredModal.buttons.no')"
    :rightButtonText="rightButtonText"
    @hideMe="[showCenteredModal = false, isDeleteOperation = false, isRemoveSignature = false]"
    @rightButtonClicked="confirmationHandler" />
    <!-- Feedback Toast -->
    <toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
    <!-- Loader Generic -->
    <loaders-generic
    v-if="showOverlayLoader"
    :isOverlay="showOverlayLoader"/>
</template>

<script setup>
import GenericDetails from '@/components/features/approvals/details/GenericDetails'
import OperatorDetails from '@/components/features/approvals/details/operatorDetails/OperatorDetails'
import ModalCenteredGeneric from '@/components/generic/modals/centered/Generic'
import LoadersGeneric from '@/components/generic/loaders/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Store */
import { mapActions } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { createDownloadGenericReceiptPdf } from '@/network/api/operations/operations.api.js'

/* Aux */
import { amountFormat } from '@/assets/scripts/amounts'
import { routeList } from '@/router/helpers/routes'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Vue */
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/** Props
@activeStep { Number } - number step
@headerOperationStatus { String } - status of header operation
@operationDetailsInfo { object } - object with info for details modal
@selectedOperationsIDs {array} - operations ids selected on pendingSignature view
@showDetailsFooter {boolean} - boolean controler to display details footer
@isMobile { boolean }
*/
const props = defineProps({
    activeStep: Number,
    headerOperationStatus: String,
    operationDetailsInfo: Object,
    selectedOperationsIDs: Array,
    showDetailsFooter: Boolean,
    isMobile: Boolean
})

const emit = defineEmits(['hideMe', 'updateList', 'success', 'selectOperation'])

const currentRoute = useRoute()

/* mapActions */
const { removeSignatureFromOperation, cancelOperation } = mapActions(moduleMapper.APPROVALS)

/* Show Data */
const showCenteredModal = ref(false)
const showOverlayLoader = ref(false)
const triggerDetailsModalClose = ref(false)

const pendingSignatureRoute = computed(() => {
    return currentRoute.path === routeList.APPROVALS_PENDING_SIGNATURE_PATH
})

/*************************************************
*                                                *
*                  Graph Data                    *
*                                                *
*************************************************/
const graphData = ref([])
const signedPercentage = ref(null)
const notSignedPercentage = ref(null)

const graphDataConstructor = () => {
    const signed = props.operationDetailsInfo.signatureList.filter(signature => signature.signDate)

    signedPercentage.value = signed.reduce((acc, obj) => {
        acc = acc + obj.weight

        if (acc > 100) acc = 100

        return acc
    }, 0)

    notSignedPercentage.value = 100 - signedPercentage.value

    graphData.value = [
        ['signedPercentage', signedPercentage.value],
        ['notSignedPercentage', notSignedPercentage.value]
    ]
}

/*************************************************
*                                                *
*                Detail Modal Info               *
*                                                *
*************************************************/
const requestAction = `${moduleMapper.APPROVALS}/requestOperationDetails`
const detailInfo = ref({})
const payloadDetailModal = ref({})
const buttonsTopData = ref({})
const buttonsFooterData = ref({})
const isDeleteOperation = ref(false)
const isRemoveSignature = ref(false)

const detailsComponent = computed(() => {
    const operationTypeId = props.operationDetailsInfo.operationTypeId

    return operationTypeId !== 501 && operationTypeId !== 503 && operationTypeId !== 504 ? GenericDetails : OperatorDetails
})

const detailHeaderStyles = computed(() => {
    if (!props.operationDetailsInfo.status.classes && props.headerOperationStatus === null) return { color: null, bigIcon: null }

    if (props.activeStep === 3 && (props.headerOperationStatus.status === 'ERROR' || props.headerOperationStatus.status === 'WARNING')) {
        return {
            color: 'var(--warning)',
            icon: 'clock'
        }
    }

    if (props.operationDetailsInfo.status.classes === 'positive') {
        return {
            color: 'var(--positive)',
            icon: 'check-circle'
        }
    } else if (props.operationDetailsInfo.status.classes === 'negative') {
        return {
            color: 'var(--negative)',
            icon: props.operationDetailsInfo.status.icon === 'status-alert' ? 'clock' : 'error'
        }
    } else {
        return {
            color: 'var(--warning)',
            icon: 'clock'
        }
    }
})

const detailSubTitle = computed(() => {
    if (props.operationDetailsInfo.amount.amount) {
        return amountFormat(Math.abs(props.operationDetailsInfo.amount.amount), props.operationDetailsInfo.amount.currency)
    } else {
        return null
    }
})

const footerButtonsForSignedByMeView = computed(() => {
    return [{
        label: t('approvals.signed.modal.details.footerButtons.delete'),
        buttonType: 'negative',
        buttonPosition: 'left',
        buttonVariant: 'only-border',
        icon: 'delete',
        callbackFunction: () => {
            showCenteredModal.value = true
            isDeleteOperation.value = true
        }
    }, {
        label: t('approvals.signed.modal.details.footerButtons.removeSignature'),
        buttonType: 'primary',
        iconPosition: 'right',
        icon: 'close',
        callbackFunction: () => {
            showCenteredModal.value = true
            isRemoveSignature.value = true
        }
    }]
})

const footerButtonsForPendingSignatureView = computed(() => {
    const isSelected = props.selectedOperationsIDs.includes(detailInfo.value.operationId)

    return [{
        label: t('approvals.signed.modal.details.footerButtons.delete'),
        buttonType: 'negative',
        buttonPosition: 'left',
        buttonVariant: 'only-border',
        icon: 'delete',
        callbackFunction: () => {
            showCenteredModal.value = true
            isDeleteOperation.value = true
        }
    }, {
        label: isSelected ? t('approvals.pendingSignature.modal.details.footerButtons.removeSelection') : t('approvals.pendingSignature.modal.details.footerButtons.select'),
        icon: isSelected ? 'close' : 'check-line',
        iconPosition: 'right',
        buttonType: 'primary',
        callbackFunction: () => {
            emit('selectOperation', detailInfo.value.operationId)
            triggerDetailsModalClose.value = true
        }
    }]
})

const setDetailInfo = () => {
    graphDataConstructor()

    detailInfo.value = {
        ...props.operationDetailsInfo,
        subTitle: detailSubTitle.value,
        headerStyles: detailHeaderStyles.value,
        graphData: graphData.value,
        signedPercentage: signedPercentage.value,
        notSignedPercentage: notSignedPercentage.value
    }

    payloadDetailModal.value = {
        operationId: props.operationDetailsInfo.id
    }

    buttonsTopData.value = {
        downloadUrl: createDownloadGenericReceiptPdf({ params: { operationID: props.operationDetailsInfo.id } }),
        emailAction: 'sendOperationEmailNotification',
        smsAction: 'sendOperationSMSNotification',
        sendPayload: { operationId: props.operationDetailsInfo.id },
        operationId: props.operationDetailsInfo.id
    }

    buttonsFooterData.value = pendingSignatureRoute.value ? footerButtonsForPendingSignatureView.value : footerButtonsForSignedByMeView.value
}

/*************************************************
*                                                *
*              Centered Modal Info               *
*                                                *
*************************************************/
const centeredModalTitle = computed(() => {
    return isDeleteOperation.value ? t('approvals.signed.modal.centeredModal.delete.title') : t('approvals.signed.modal.centeredModal.removeSignature.title')
})

const centeredModalText = computed(() => {
    return isDeleteOperation.value ? t('approvals.signed.modal.centeredModal.delete.text') : t('approvals.signed.modal.centeredModal.removeSignature.text')
})

const rightButtonText = computed(() => {
    return isDeleteOperation.value ? t('approvals.signed.modal.centeredModal.buttons.deleteYes') : t('approvals.signed.modal.centeredModal.buttons.removeYes')
})

const removeSignatureRequest = async () => {
    try {
        const res = await removeSignatureFromOperation({ id: detailInfo.value.operationId })
        showOverlayLoader.value = false
        isRemoveSignature.value = false
        emit('success', res.data)
        emit('updateList')
    } catch (error) {
        showOverlayLoader.value = false
        isRemoveSignature.value = false
		errorHandler(error.response.data, removeSignatureRequest)
    }
}

const deleteOperationRequest = async () => {
    try {
        const res = await cancelOperation({ id: detailInfo.value.operationId })
        showOverlayLoader.value = false
        isDeleteOperation.value = false
        emit('success', res.data)
        emit('updateList')
    } catch (error) {
        showOverlayLoader.value = false
        isDeleteOperation.value = false
		errorHandler(error.response.data, deleteOperationRequest)
    }
}

const confirmationHandler = () => {
    showOverlayLoader.value = true
    showCenteredModal.value = false

    if (isRemoveSignature.value) {
        removeSignatureRequest()
    } else {
        deleteOperationRequest()
    }
}

/*************************************************
 *                                                *
 *                 Error Handler                  *
 *                                                *
 *************************************************/
const showFeedbackToast = ref(false)

const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

setDetailInfo()
</script>

<style lang="scss" scoped>

</style>
